
import { Shell } from "@/components/layout/Shell";
import { Tabs, TabsContent, TabsList, TabsTrigger, TabsGroup } from "@/components/ui/tabs";
import { AdminDashboard } from "@/components/admin/AdminDashboard";
import { TranslationsManager } from "@/components/admin/TranslationsManager";
import { LogsViewer } from "@/components/admin/LogsViewer";
import { UserManagement } from "@/components/admin/user-management/UserManagement";
import { TestActions } from "@/components/admin/TestActions";
import { EmailSettings } from "@/components/admin/EmailSettings";
import { SeedTranslations } from "@/components/admin/SeedTranslations";
import { BugReportsManager } from "@/components/admin/bug-reports/BugReportsManager";
import { SystemSettings } from "@/components/admin/SystemSettings";
import { FeaturesSettings } from "@/components/admin/FeaturesSettings";
import { CompanyRegisterSettings } from "@/components/admin/CompanyRegisterSettings";
import { WidgetDesigner } from "@/components/admin/widget-designer/WidgetDesigner";
import { SubscriptionsManager } from "@/pages/admin/SubscriptionsManager";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useEffect, useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, Database, CreditCard } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

export default function AdminPage() {
  const { useHasRoleQuery } = useUserRoles();
  const { data: isDeveloper } = useHasRoleQuery("DEVELOPER");
  const { data: isAdmin } = useHasRoleQuery("ADMIN");
  const { data: isTester } = useHasRoleQuery("TESTER");
  const [activeTab, setActiveTab] = useState("dashboard");
  const navigate = useNavigate();
  const location = useLocation();
  
  const isFullAdmin = isDeveloper || isAdmin;
  const hasAdminAccess = isFullAdmin || isTester;

  const handleTabSelect = (value: string) => {
    if (value === "api-integration") {
      navigate("/admin/api-integration");
    } else if (value === "subscriptions") {
      // Show the subscriptions content in the current admin page
      setActiveTab("subscriptions");
    } else {
      setActiveTab(value);
    }
  };
  
  useEffect(() => {
    if (isTester && !isFullAdmin) {
      setActiveTab("bugs");
    }
  }, [isTester, isFullAdmin]);
  
  // Set subscriptions tab as active when returning from subscription plan pages
  useEffect(() => {
    if (location.pathname === "/admin" && location.state?.from === "subscription-plans") {
      setActiveTab("subscriptions");
    }
  }, [location]);

  if (!hasAdminAccess) {
    return (
      <Shell className="p-4 md:p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            You don't have permission to access the admin area.
          </AlertDescription>
        </Alert>
      </Shell>
    );
  }

  const contentTabs = [
    { label: "Translations", value: "translations" },
    { label: "Email", value: "email" },
    { label: "Logs", value: "logs" },
  ];

  const configTabs = [
    { label: "Tools", value: "tools" },
    { label: "Features", value: "features" },
    { label: "Actions", value: "actions" },
    { label: "Widgets", value: "widgets" },
  ];

  return (
    <Shell className="p-4 md:p-6">
      <h1 className="text-3xl font-bold tracking-tight mb-6">
        {isTester && !isFullAdmin ? "Bug Reports" : "Admin Dashboard"}
      </h1>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="relative mb-6">
          <div className="overflow-x-auto pb-1 scrollbar-none">
            <TabsList className="inline-flex min-w-full">
              {isFullAdmin && (
                <>
                  <TabsTrigger value="dashboard" className="min-w-[100px]">Dashboard</TabsTrigger>
                  <TabsTrigger value="users" className="min-w-[100px]">Users</TabsTrigger>
                  <TabsTrigger 
                    value="subscriptions" 
                    className="min-w-[100px]"
                  >
                    <CreditCard className="mr-2 h-4 w-4" /> Subscriptions
                  </TabsTrigger>
                  <TabsTrigger 
                    value="api-integration" 
                    onClick={() => navigate("/admin/api-integration")} 
                    className="min-w-[100px]"
                  >
                    <Database className="mr-2 h-4 w-4" /> API Integrations
                  </TabsTrigger>
                  <TabsGroup 
                    label="Content" 
                    options={contentTabs}
                    activeTab={activeTab}
                    onSelect={handleTabSelect}
                    className="min-w-[120px]"
                  />
                  <TabsGroup 
                    label="Config" 
                    options={configTabs}
                    activeTab={activeTab}
                    onSelect={handleTabSelect}
                    className="min-w-[120px]"
                  />
                </>
              )}
              <TabsTrigger value="bugs" className="min-w-[100px]">Bug Reports</TabsTrigger>
            </TabsList>
          </div>
          <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-background to-transparent pointer-events-none"></div>
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none"></div>
        </div>
        
        {isFullAdmin && (
          <>
            <TabsContent value="dashboard">
              <AdminDashboard />
            </TabsContent>
            
            <TabsContent value="users">
              <UserManagement />
            </TabsContent>
            
            <TabsContent value="subscriptions">
              <SubscriptionsManager />
            </TabsContent>
            
            <TabsContent value="translations">
              <div className="space-y-6">
                <SeedTranslations />
                <TranslationsManager />
              </div>
            </TabsContent>
            
            <TabsContent value="logs">
              <LogsViewer />
            </TabsContent>
            
            <TabsContent value="email">
              <EmailSettings />
            </TabsContent>
            
            <TabsContent value="tools">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="col-span-2">
                  <SystemSettings />
                </div>
                <div className="col-span-2 mt-6">
                  <CompanyRegisterSettings />
                </div>
                <div className="col-span-2 mt-6">
                  <h2 className="text-xl font-semibold mb-4">Additional Tools</h2>
                  <p className="text-muted-foreground">
                    More administrative tools will appear here.
                  </p>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="features">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="col-span-2">
                  <FeaturesSettings />
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="actions">
              <TestActions />
            </TabsContent>

            <TabsContent value="widgets">
              <WidgetDesigner />
            </TabsContent>
          </>
        )}
        
        <TabsContent value="bugs">
          <BugReportsManager />
        </TabsContent>
      </Tabs>
    </Shell>
  );
}
